import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Stack,
  Box,
} from '@mui/material';
import ServiceConfigFormattedMessages from '../../../../messages/admin/ServiceConfigFormattedMessages';
import useRouteRequest from '../../../../requests/RouteRequest';
import CustomModal from '../../../common/CustomModal';

const RouteEdit = ({ show, route, options, onClose, errorHandler }) => {
  const [routeId, setRouteId] = useState('');
  const [routeName, setRouteName] = useState('');
  const [routeClass, setRouteClass] = useState('');
  const [routeType, setRouteType] = useState('');
  const [routeStatus, setRouteStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [acceptsAlphas, setAcceptsAlphas] = useState(false);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const { updateRoute, createRoute } = useRouteRequest();

  useEffect(() => {
    if (route) {
      setRouteId(route.route_id ?? '');
      setRouteName(route.route_name ?? '');
      setRouteClass(route.route_class ?? '');
      setRouteType(route.route_type ?? '');
      setRouteStatus(route.route_status ?? '');
      setPriority(route.priority ?? '');
      setAcceptsAlphas(route.accepts_alphas === 'Y');
    }
  }, [route]);

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;

    switch (name) {
      case 'route_id':
        setRouteId(value);
        break;
      case 'route_name':
        setRouteName(value);
        break;
      case 'route_class':
        setRouteClass(value);
        break;
      case 'route_type':
        setRouteType(value);
        break;
      case 'route_status':
        setRouteStatus(value);
        break;
      case 'priority':
        setPriority(value);
        break;
      case 'accepts_alphas':
        setAcceptsAlphas(type === 'checkbox' ? checked : value);
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = document.getElementById('routeForm');
    if (!form.checkValidity()) {
      setValidated(true);
      return;
    }

    setLoading(true);
    const routeData = {
      route_id: routeId,
      route_name: routeName,
      route_class: routeClass,
      route_type: routeType,
      route_status: routeStatus,
      priority: priority,
      accepts_alphas: acceptsAlphas ? 'Y' : 'N',
    };

    try {
      if (route.route_id) {
        await updateRoute(routeId, routeData, errorHandler);
      } else {
        await createRoute(routeData, errorHandler);
      }
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomModal
      title={route.route_id ? 'Edit Route' : 'New Route'}
      open={show}
      width={600}
      handleClose={onClose}
    >
      <form id="routeForm" onSubmit={handleSubmit}>
        <Stack gap={2}>
          <TextField
            margin="dense"
            id="routeId"
            label={ServiceConfigFormattedMessages.routeId}
            type="text"
            fullWidth
            required
            size="small"
            value={routeId}
            onChange={(e) => handleInputChange(e)}
            error={routeId !== '' && !/^[0-9]+$/.test(routeId)}
            helperText={
              routeId !== '' && !/^[0-9]+$/.test(routeId)
                ? 'Please enter a valid number'
                : ''
            }
          />

          <TextField
            margin="dense"
            id="routeName"
            label={ServiceConfigFormattedMessages.routeName}
            type="text"
            fullWidth
            size="small"
            required
            value={routeName}
            onChange={(e) => handleInputChange(e)}
          />

          <TextField
            select
            label={ServiceConfigFormattedMessages.class}
            id="routeClass"
            value={routeClass}
            onChange={(e) => handleInputChange(e)}
            size="small"
            name="route_class"
            fullWidth
          >
            <MenuItem value="">
              <em>{ServiceConfigFormattedMessages.class}</em>
            </MenuItem>
            {options?.ROUTE_CLASS &&
              Object.keys(options.ROUTE_CLASS).map((key) => (
                <MenuItem key={key} value={key}>
                  {options.ROUTE_CLASS[key]}
                </MenuItem>
              ))}
          </TextField>

          <TextField
            select
            label={ServiceConfigFormattedMessages.type}
            id="routeType"
            value={routeType}
            size="small"
            onChange={(e) => handleInputChange(e)}
            name="route_type"
            fullWidth
          >
            <MenuItem value="">
              <em>{ServiceConfigFormattedMessages.type}</em>
            </MenuItem>
            {options?.ROUTE_TYPE &&
              Object.keys(options.ROUTE_TYPE).map((key) => (
                <MenuItem key={key} value={key}>
                  {options.ROUTE_TYPE[key]}
                </MenuItem>
              ))}
          </TextField>

          <TextField
            select
            label={ServiceConfigFormattedMessages.status}
            id="routeStatus"
            value={routeStatus}
            size="small"
            onChange={(e) => handleInputChange(e)}
            name="route_status"
            fullWidth
          >
            <MenuItem value="" selected>
              <em>{ServiceConfigFormattedMessages.status}</em>
            </MenuItem>
            {options?.ROUTE_STATUS &&
              Object.keys(options.ROUTE_STATUS).map((key) => (
                <MenuItem key={key} value={key}>
                  {options.ROUTE_STATUS[key]}
                </MenuItem>
              ))}
          </TextField>

          <TextField
            margin="dense"
            id="priority"
            label={ServiceConfigFormattedMessages.priority}
            type="text"
            fullWidth
            required
            value={priority}
            size="small"
            onChange={(e) => handleInputChange(e)}
            error={priority !== '' && !/^[0-9]+$/.test(priority)}
            helperText={
              priority !== '' && !/^[0-9]+$/.test(priority)
                ? 'Please enter a valid number'
                : ''
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptsAlphas}
                onChange={(e) => handleInputChange(e)}
                name="accepts_alphas"
              />
            }
            label={ServiceConfigFormattedMessages.acceptsAlphas}
          />
        </Stack>
      </form>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'end' }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </Box>
    </CustomModal>
  );
};

export default RouteEdit;
